/**
 * System Health Dashboard
 * Real-time monitoring of infrastructure dependencies and activity events
 */

import React, { useEffect } from 'react';
import { Loader } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { useHealthData } from './hooks/useHealthData';
import { useLatencyHistory } from './hooks/useLatencyHistory';
import HealthHeader from './components/HealthHeader';
import DependencyGrid from './components/DependencyGrid';
import ActivityTimeline from './components/ActivityTimeline';
import styles from './SystemHealth.module.css';

const I18N_PREFIX = 'systemSettings.systemHealth';

const SystemHealth: React.FC = () => {
  const {
    data,
    isLoading,
    isRefreshing,
    error,
    lastUpdated,
    secondsUntilRefresh,
    refresh
  } = useHealthData();

  const { history, addEntry } = useLatencyHistory();

  // Add latency entry when data updates
  useEffect(() => {
    if (data?.dependencies) {
      addEntry(data.dependencies);
    }
  }, [data, addEntry]);

  // Initial loading state
  if (isLoading && !data) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Loader active inline="centered" size="large" />
          <p className={styles.loadingText}>{I18n.t(`${I18N_PREFIX}.loading`)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <HealthHeader
        data={data}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        error={error}
        lastUpdated={lastUpdated}
        secondsUntilRefresh={secondsUntilRefresh}
        onRefresh={refresh}
      />

      {data && (
        <>
          <DependencyGrid
            dependencies={data.dependencies}
            history={history}
            isRefreshing={isRefreshing}
          />

          <ActivityTimeline events={data.events} />

          {/* Footer with system info */}
          <div className={styles.footer}>
            <div className={styles.footerItem}>
              <span className={styles.footerLabel}>{I18n.t(`${I18N_PREFIX}.footer.uptime`)}</span>
              <span className={styles.footerValue}>{parseUptime(data.uptime)}</span>
            </div>
            <div className={styles.footerDivider} />
            <div className={styles.footerItem}>
              <span className={styles.footerLabel}>{I18n.t(`${I18N_PREFIX}.footer.serverTime`)}</span>
              <span className={styles.footerValue}>
                {new Date(data.timestamp).toLocaleString()}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper to parse uptime format
const parseUptime = (uptime: string): string => {
  const match = uptime.match(/(\d+)\.(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return uptime;
  
  const [, days, hours, minutes, seconds] = match;
  const parts: string[] = [];
  
  if (parseInt(days) > 0) parts.push(`${parseInt(days)}d`);
  if (parseInt(hours) > 0) parts.push(`${parseInt(hours)}h`);
  if (parseInt(minutes) > 0) parts.push(`${parseInt(minutes)}m`);
  if (parseInt(seconds) > 0 || parts.length === 0) parts.push(`${parseInt(seconds)}s`);
  
  return parts.join(' ');
};

export default SystemHealth;
